import AbstractContract, {
  ContractInstances,
} from '@swarm/core/contracts/AbstractContract'
import { BPoolAddresses } from '@swarm/core/contracts/BPoolAddresses'
import { getCpk } from '@swarm/core/contracts/cpk'
import { getCurrentConfig } from '@swarm/core/observables/configForNetwork'
import { invalidateKeys } from '@swarm/core/observables/watcher'
import { getBalanceCacheKey } from '@swarm/core/services/cache/utils'
import { verify } from '@swarm/core/shared/utils/crypto'
import { big, denormalize } from '@swarm/core/shared/utils/helpers/big-helpers'
import { getAccount, isSameEthereumAddress } from '@swarm/core/web3'
import { NormalizedStake } from '@swarm/types/normalized-entities'
import { AbstractToken } from '@swarm/types/tokens'
import { BigSource } from 'big.js'
import { TransactionResult } from 'contract-proxy-kit'
import { Address } from 'contract-proxy-kit/src/utils/basicTypes'
import { utils } from 'ethers'

import abi from 'src/contracts/abi'

import type { SwarmStaking as SwarmStakingContractType } from './typechain/SwarmStaking'

const { swarmStakingAddress } = getCurrentConfig()
const SwarmStakingInterface = new utils.Interface(abi.SwarmStaking)

export class SwarmStakingContract extends AbstractContract {
  contract: SwarmStakingContractType | undefined

  static instances: ContractInstances<SwarmStakingContract> = {}

  constructor() {
    super(swarmStakingAddress, abi.SwarmStaking)
  }

  static getInstance = async (): Promise<SwarmStakingContract> => {
    if (!SwarmStakingContract.instances[swarmStakingAddress]) {
      SwarmStakingContract.instances[swarmStakingAddress] =
        new SwarmStakingContract()
      await SwarmStakingContract.instances[swarmStakingAddress].init()
    }
    return SwarmStakingContract.instances[swarmStakingAddress]
  }

  private enterStaking = async (
    security: Address,
    stakeAmount: BigSource,
    smt: AbstractToken,
  ): Promise<TransactionResult> => {
    this.init()
    this.updateSigner()

    if (this.contract === undefined) {
      throw new Error('SwarmStaking contract is not initialized')
    }

    const denormStakeAmount = denormalize(stakeAmount, smt.decimals)
    const resp = await this.contract.enterStaking(
      security,
      denormStakeAmount.toFixed(0),
    )

    const account = getAccount()
    if (account) {
      invalidateKeys(getBalanceCacheKey(account, smt.id, this.address))
    }

    return resp
  }

  private exitStaking = async (
    stake: NormalizedStake,
    receiver: Address,
  ): Promise<TransactionResult> => {
    this.init()
    this.updateSigner()

    // Verify the CPK is defined
    const cpk = getCpk()
    const account = getAccount()
    const smtId = await BPoolAddresses.getUtilityTokenAddress()

    verify(!!cpk, 'Could not obtain CPK')

    if (this.contract === undefined) {
      throw new Error('SwarmStaking contract is not initialized')
    }

    const stakingId = stake.id

    if (isSameEthereumAddress(stake.maker, account)) {
      // ALTA we used CPK to enter staking. We don't do that now, but we need to allow users to unstake their SMTs
      const resp = await this.contract.exitStaking(stakingId)

      if (cpk.address) {
        invalidateKeys(getBalanceCacheKey(cpk.address, smtId, this.address))
      }
      return resp
    }

    const params = [stakingId]
    cpk?.patchTxs({
      to: swarmStakingAddress,
      data: SwarmStakingInterface.encodeFunctionData('exitStaking', params),
    })

    cpk.transferToken(receiver, smtId, big(stake.stakedAmount))

    const resp = await cpk?.execStoredTxs()

    if (cpk.address) {
      invalidateKeys(getBalanceCacheKey(cpk.address, smtId, this.address))
    }

    return resp
  }

  static enterStaking = async (
    security: Address,
    stakeAmount: BigSource,
    smt: AbstractToken,
  ) => {
    const otcContract = await SwarmStakingContract.getInstance()
    return otcContract.enterStaking(security, stakeAmount, smt)
  }

  static exitStaking = async (stake: NormalizedStake, receiver: Address) => {
    const otcContract = await SwarmStakingContract.getInstance()
    return otcContract.exitStaking(stake, receiver)
  }
}
